import Cross from "@icons/cross.svg?react";
import DocumentSvg from "@icons/document.svg?react";
import DownloadSvg from "@icons/download.svg?react";
import UsdCircleSvg from "@icons/usd-circle.svg?react";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import {
  keepPreviousData,
  useQuery,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { DateTime } from "luxon";
import React from "react";
import { ErrorBoundary } from "react-error-boundary";
import { FormProvider, useForm, UseFormReturn } from "react-hook-form";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import invariant from "tiny-invariant";

import { Button } from "@/common/components/button/button";
import { Dialog } from "@/common/components/dialog/dialog";
import { Failed } from "@/common/components/info/info";
import * as OptionList from "@/common/components/option-list/option-list";
import { PriceChart } from "@/common/components/price-chart/price-chart";
import { PropertiesList } from "@/common/components/properties-list/properties-list";
import { Params } from "@/common/components/quick-filters/quick-filters";
import { useCurrentDateRange } from "@/common/components/quick-filters/quick-filters.hooks";
import { SearchParamsLink } from "@/common/components/search-params-link/search-params-link";
import { Spacer } from "@/common/components/spacer/spacer";
import { Spinner } from "@/common/components/spinner/spinner";
import { useCurrency } from "@/common/hooks/use-currency";
import { useRequiredErpCompany } from "@/common/hooks/use-erp-company";
import { useLanguage } from "@/common/hooks/use-language";
import { currencyFormatter } from "@/common/services/formatter";
import { CropCategory, Currency } from "@/generated/digitalnisklady.cz/graphql";
import { FullscreenModalLayout } from "@/routes/layout/layout.fullscreen";
import { CropParameters } from "@/routes/shared/components/crop-parameters/crop-parameters";
import { usePostalCodeDialogParams } from "@/routes/shared/components/postal-code-dialog/postal-code-dialog";
import { usePostalCodeObject } from "@/routes/shared/components/postal-code-dialog/use-postal-code-object";
import { getUnit } from "@/routes/shared/utils/crop-unit";

import { formatDiscount } from "./discount-formatter";
import {
  marketplaceItemPriceHistoryQuery,
  marketplaceItemQuery,
  marketplaceItemTraderPricesQuery,
} from "./marketplace.detail.api";
import { formatTradingHours, isWithinTradingHours } from "./time-validation";

const path = ":cropId/quarter/:quarterId";

const getPriceDecimalPlaces = (
  category: CropCategory,
  currency: string,
): number => {
  if (category === CropCategory.Fuel) {
    return 2;
  }
  return currency === "CZK" ? 0 : 2;
};

type Fields = {
  marketPriceId: string;
};

const MarketplaceDetail = () => {
  const { cropId, quarterId } = usePageParams();
  const { params } = usePostalCodeDialogParams();
  const postalCode = usePostalCodeObject(params?.postalCode);
  const { data: marketplaceItem } = useSuspenseQuery(
    marketplaceItemQuery(cropId),
  );
  const handleBuy = useBuyRoute();
  const [, currency] = useCurrency();
  const locale = useLanguage();
  const traderPrices = useSuspenseQuery(
    marketplaceItemTraderPricesQuery({
      cropId,
      currency: currency as Currency,
      latitude: postalCode.latitude!,
      locale,
      longitude: postalCode.longitude!,
      quarterId,
    }),
  );
  const parameters = [
    {
      label: t`Kvartál`,
      value: traderPrices.data.at(0)?.quarter.name,
    },
  ];
  const isReady = marketplaceItem.traders.length > 0 && postalCode.valid;
  const cheapestPrice = traderPrices.data.at(0);
  const form = useForm<Fields>({
    defaultValues: {
      marketPriceId: cheapestPrice?.id,
    },
  });
  const currentMarketPriceId = form.watch("marketPriceId");
  const currentTraderPrice = traderPrices.data.find(
    (price) => price.id === currentMarketPriceId,
  );
  const [showTimeRestrictionDialog, setShowTimeRestrictionDialog] =
    React.useState(false);

  React.useEffect(() => {
    if (cheapestPrice) {
      form.reset({
        marketPriceId: cheapestPrice.id,
      });
    }
  }, [cheapestPrice, form]);

  const buyContext = {
    ...currentTraderPrice,
    category: marketplaceItem.category,
    item: {
      id: marketplaceItem.id,
      name: marketplaceItem.name,
      parameters,
    },
    latitude: postalCode.latitude!,
    longitude: postalCode.longitude!,
    marketPriceId: currentMarketPriceId,
    postalCode: postalCode.code,
    postalCodeLabel: postalCode.label,
    quarterName: traderPrices.data.at(0)?.quarter.name,
  };

  return (
    <>
      <FullscreenModalLayout
        headerButton={
          <SearchParamsLink omit={["date-from", "date-to"]} to="..">
            <Cross />
          </SearchParamsLink>
        }
      >
        <FullscreenModalLayout.Title>
          {marketplaceItem.name}
        </FullscreenModalLayout.Title>
        <PropertiesList properties={parameters} />
        <CropParameters
          cropId={cropId}
          traderId={currentTraderPrice?.trader.id}
        />
        {isReady ? (
          <>
            <Params>
              <ErrorBoundary
                fallbackRender={({ error }) => <Failed error={error} />}
              >
                <Spacer className="h-6" />
                <h2 className="text-can-forest-teal font-bold">
                  <Trans>Vývoj aktuální ceny</Trans>
                </h2>
                <Spacer className="h-4" />
                {currentTraderPrice ? (
                  <Chart
                    postalCode={{
                      latitude: postalCode.latitude!,
                      longitude: postalCode.longitude!,
                    }}
                    traderId={currentTraderPrice.trader.id}
                  />
                ) : null}
              </ErrorBoundary>
            </Params>
            <Spacer className="h-6" />
            <h2 className="text-can-forest-teal font-bold">
              <Trans>Srovnání cen</Trans>
            </h2>
            <Spacer className="h-4" />
            {cheapestPrice ? (
              <TraderForm form={form}>
                <form aria-label="trader-choice">
                  <OptionList.List>
                    {traderPrices.data.map(({ id, price, trader }) => (
                      <OptionList.Item key={trader.id}>
                        <OptionList.Label>
                          <OptionList.Checkbox
                            name="marketPriceId"
                            options={{
                              required: t`Vyberte prosím obchodníka`,
                            }}
                            value={id}
                          >
                            <span className="flex items-center">
                              <img
                                alt={trader.name}
                                src={trader.logo}
                                style={{ height: "20px" }}
                              />
                            </span>
                          </OptionList.Checkbox>
                          <span
                            className={`flex items-end flex-col ${currentMarketPriceId === id ? "text-can-forest-teal" : ""}`}
                          >
                            <span className="font-bold">
                              {currencyFormatter(currency, {
                                maximumFractionDigits: getPriceDecimalPlaces(
                                  marketplaceItem.category,
                                  currency,
                                ),
                              }).format(price)}
                              /{getUnit(trader.unit)}
                            </span>
                            {trader.minimumAmount ? (
                              <span className="text-xs">
                                <Trans>Min. odběr:</Trans>{" "}
                                {trader.minimumAmount} {getUnit(trader.unit)}
                              </span>
                            ) : null}
                          </span>
                        </OptionList.Label>
                        {(trader.description ||
                          trader.urlInfo ||
                          trader.discounts.length > 0) &&
                        currentMarketPriceId === id ? (
                          <div className="px-4 pb-4 flex flex-col gap-3">
                            {trader.image ? (
                              <img
                                className="max-h-[200px] bg-white object-contain"
                                src={trader.image}
                              />
                            ) : null}
                            {trader.description ? (
                              <p>
                                <small
                                  className="text-sm"
                                  dangerouslySetInnerHTML={{
                                    __html: trader.description,
                                  }}
                                />
                              </p>
                            ) : null}
                            {trader.discounts.length > 0 ? (
                              <div className="flex flex-col gap-1">
                                <span className="text-sm flex items-center font-bold text-can-forest-teal gap-2">
                                  <UsdCircleSvg className="[&_path]:fill-can-forest-teal w-4 h-4" />
                                  <Trans>Množstevní slevy</Trans>
                                </span>
                                {trader.discounts.map((discount) => (
                                  <span className="text-sm" key={discount.id}>
                                    {formatDiscount(
                                      discount,
                                      price,
                                      discount.currency ?? currency,
                                      getUnit(trader.unit),
                                      marketplaceItem.category,
                                    )}
                                  </span>
                                ))}
                              </div>
                            ) : null}
                            {trader.urlInfo ? (
                              <p>
                                <a
                                  className="flex gap-2"
                                  href={trader.urlInfo}
                                  rel="noreferrer"
                                  target="_blank"
                                >
                                  <DocumentSvg />
                                  <small className="text-sm font-bold text-can-forest-teal">
                                    <Trans>Úplné složení ke stažení</Trans>
                                  </small>
                                  <DownloadSvg className="ml-auto" />
                                </a>
                              </p>
                            ) : null}
                          </div>
                        ) : null}
                      </OptionList.Item>
                    ))}
                  </OptionList.List>
                  <Spacer className="h-4" />
                </form>
                <Button.Container>
                  <Button
                    onClick={form.handleSubmit(async () => {
                      const timeFrom =
                        currentTraderPrice?.trader.timeFromTrading;
                      const timeTo = currentTraderPrice?.trader.timeToTrading;

                      if (!isWithinTradingHours(timeFrom, timeTo)) {
                        setShowTimeRestrictionDialog(true);
                        return;
                      }

                      await handleBuy();
                    })}
                    type="submit"
                    variant="primary"
                  >
                    <Trans>Nakoupit</Trans>
                  </Button>
                </Button.Container>
              </TraderForm>
            ) : null}
          </>
        ) : null}
      </FullscreenModalLayout>
      {showTimeRestrictionDialog ? (
        <Dialog>
          <Dialog.Title>
            <Trans>Obchodování není možné</Trans>
          </Dialog.Title>
          <p>
            {(() => {
              const tradingHours = formatTradingHours(
                currentTraderPrice?.trader.timeFromTrading,
                currentTraderPrice?.trader.timeToTrading,
              );
              return (
                <Trans>
                  S tímto obchodníkem je možné obchodovat pouze v čase{" "}
                  {tradingHours}.
                </Trans>
              );
            })()}
          </p>
          <Dialog.Actions>
            <Dialog.Confirm onClick={() => setShowTimeRestrictionDialog(false)}>
              <Trans>Rozumím</Trans>
            </Dialog.Confirm>
          </Dialog.Actions>
        </Dialog>
      ) : null}
      <Outlet context={buyContext} />
    </>
  );
};

const TraderForm = ({
  form,
  children,
}: React.PropsWithChildren<{ form: UseFormReturn<Fields> }>) => {
  return <FormProvider {...form}>{children}</FormProvider>;
};

const Chart = ({
  traderId,
  postalCode,
}: {
  postalCode: { latitude: number; longitude: number };
  traderId: string;
}) => {
  const { id: companyId } = useRequiredErpCompany();
  const { cropId, quarterId } = usePageParams();
  const [, currency] = useCurrency();
  const { dateFrom, dateTo } = useCurrentDateRange();
  const prices = useQuery({
    ...marketplaceItemPriceHistoryQuery({
      aggregated: true,
      companyId,
      cropId,
      currency: currency as Currency,
      from: DateTime.fromISO(dateFrom).toSeconds().toString(),
      latitude: postalCode.latitude,
      longitude: postalCode.longitude,
      quarterId,
      to: DateTime.fromISO(dateTo).toSeconds().toString(),
      traderIds: [traderId],
    }),
    placeholderData: keepPreviousData,
  });

  if (prices.status === "pending") {
    return <Spinner />;
  }

  if (prices.status === "error") {
    return <Failed error={prices.error} />;
  }

  if (prices.isPlaceholderData) {
    return (
      <div className="relative">
        <div className="blur-xs">
          <PriceChart prices={prices.data} />
        </div>
        <div className="absolute left-0 right-0 top-0 bottom-0 flex flex-col justify-center">
          <Spinner />
        </div>
      </div>
    );
  }

  return <PriceChart prices={prices.data} />;
};

const usePageParams = () => {
  const { cropId, quarterId } = useParams();
  // eslint-disable-next-line lingui/no-unlocalized-strings
  invariant(cropId, "cropId is required");
  // eslint-disable-next-line lingui/no-unlocalized-strings
  invariant(quarterId, "quarterId is required");

  return { cropId, quarterId };
};

const useBuyRoute = () => {
  const { search } = useLocation();
  const navigate = useNavigate();

  return async () => {
    await navigate({
      pathname: "buy",
      search,
    });
  };
};

export { MarketplaceDetail, path };
